import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Combobox } from "@headlessui/react";
import SearchIcon from "src/assets/down-arrow.svg";
import { Spinner } from "src/components";

interface GridProductAutocompleteProps {
  placeholder?: string;
  suggestionList: any[];
  isLoading?: boolean;
  displayKey: "companyProducts.name" | "companyProducts.item_code";
  selectedValue: { product_name?: string; item_code?: string } | null;
  onSelectionChange: (value: any) => void;
}

const getValue = (obj: any, path: string): string => {
  if (!obj) return "";
  return path.split(".").reduce((acc: any, k) => (acc == null ? acc : acc[k]), obj) ?? "";
};

const GridProductAutocomplete: FC<GridProductAutocompleteProps> = ({
  placeholder = "Select",
  suggestionList,
  isLoading = false,
  displayKey,
  selectedValue,
  onSelectionChange,
}) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, "");
    if (!Array.isArray(suggestionList)) return [];
    if (!q) return suggestionList;
    return suggestionList.filter((item) => {
      const name = getValue(item, "companyProducts.name").toString().toLowerCase().replace(/\s+/g, "");
      const code = getValue(item, "companyProducts.item_code").toString().toLowerCase().replace(/\s+/g, "");
      return name.includes(q) || code.includes(q);
    });
  }, [suggestionList, query]);

  const updateCoords = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useLayoutEffect(() => {
    updateCoords();
    const onScrollOrResize = () => updateCoords();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  // Construct a synthetic value reflecting the parent's selectedValue so the
  // Combobox.Input's displayValue renders the right text. The reference-based
  // identity match in Combobox is unused here (parent stores derived fields,
  // not the full product object), so this is purely for display.
  const syntheticValue = useMemo(
    () =>
      selectedValue && (selectedValue.product_name || selectedValue.item_code)
        ? {
            companyProducts: {
              name: selectedValue.product_name || "",
              item_code: selectedValue.item_code || "",
            },
          }
        : null,
    [selectedValue]
  );

  const displayValue = (item: any): string => {
    if (!item) return "";
    if (displayKey === "companyProducts.item_code") {
      return getValue(item, "companyProducts.item_code") || "";
    }
    return getValue(item, "companyProducts.name") || "";
  };

  return (
    <Combobox
      value={syntheticValue}
      onChange={(value: any) => {
        if (value) onSelectionChange(value);
        setQuery("");
      }}
      nullable
    >
      {({ open }) => (
        <div className="relative w-full">
          <Combobox.Input
            ref={inputRef as any}
            displayValue={displayValue}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => updateCoords()}
            placeholder={placeholder}
            className="border font-semibold w-full rounded-lg focus:bg-white focus:outline-none text-sm disabled:bg-gray-50 text-[#1E1E1E] px-3 py-2 pr-8"
            autoComplete="off"
          />
          <Combobox.Button
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            onClick={() => updateCoords()}
            // tabIndex={-1}
          >
            <img src={SearchIcon} alt="dropdown" />
          </Combobox.Button>

          {open &&
            createPortal(
              <Combobox.Options
                style={{
                  position: "fixed",
                  top: coords.top,
                  left: coords.left,
                  width: coords.width,
                  zIndex: 9999,
                }}
                className="bg-white border max-h-60 overflow-y-auto rounded-md py-2 text-base shadow-lg sm:text-sm focus:outline-none"
              >
                {isLoading && (
                  <div className="flex justify-center my-3">
                    <Spinner size="medium" />
                  </div>
                )}
                {!isLoading && filtered.length === 0 && (
                  <p className="text-sm font-semibold text-center text-gray-600 py-2">
                    {query ? "Not found..." : "No Data..."}
                  </p>
                )}
                {!isLoading &&
                  filtered.map((item) => {
                    const name = getValue(item, "companyProducts.name");
                    const code = getValue(item, "companyProducts.item_code");
                    if (!name && !code) return null;
                    return (
                      <Combobox.Option
                        key={item?.id ?? `${name}-${code}`}
                        value={item}
                        className={({ active }) =>
                          `px-3 py-2 cursor-pointer font-semibold ${
                            active ? "bg-purple-50 text-purple-700" : ""
                          }`
                        }
                      >
                        {displayKey === "companyProducts.item_code"
                          ? `${code}${name ? ` - ${name}` : ""}`
                          : `${name}${code ? ` - ${code}` : ""}`}
                      </Combobox.Option>
                    );
                  })}
              </Combobox.Options>,
              document.body
            )}
        </div>
      )}
    </Combobox>
  );
};

export default GridProductAutocomplete;
